import { useEffect, useMemo, useState } from 'react';
import { useEmployer } from '../context/EmployerContext';
import { useDrawer } from '../../drawer/DrawerContext';
import { useToast } from '../../toast/ToastContext';
import { parseCsv } from '../../icib-admin/utils/csv';
import Pagination, { usePagination } from '../../icib-admin/components/Pagination';
import DefinitionList from '../../icib-admin/components/DefinitionList';
import LifeRail from '../../icib-admin/components/LifeRail';
import { fmtDate, fmtDT, resultRowEmail } from '../../icib-admin/utils/format';
import { withProductColumns, productDropdowns, prepareUploadRows, columnHeadings, withHeadings, fromHeadings } from '../../icib-admin/utils/productColumns';
// utils/xlsx.js pulls in exceljs purely to build/read the one .xlsx
// template — dynamically imported below so it's its own chunk. Same
// reasoning/module as ICIB-02's IntakeView and EMP-03's NewStarterView,
// reused as-is rather than duplicated per dashboard.
const loadXlsxUtils = () => import('../../icib-admin/utils/xlsx');

// Today's date in NZ time (YYYY-MM-DD) — same helper/reasoning as
// ICIB-02's IntakeView.
function today() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Pacific/Auckland' }).format(new Date());
}

// REMOVAL bulk-upload columns (BrandonUserStoryAPI.md EMP-04 / README
// §2.6, §2.8) — email plus subsidised_product_level (ICIB-174 — a JSON
// array of one or more product ids, identifying which enrolment(s) to
// end) plus the two §2.6a fields; no personal-detail or form_data
// columns, since a Removal never touches either. In the .xlsx the
// subsidised_product_level slot becomes one Yes/No column per product and
// company_code isn't sent at upload — see utils/productColumns.js. Column
// order matches ICIB-02's IntakeView (EXTR-13 — ICIB's Additions/Removals
// spreadsheet layout).
const REMOVAL_COLUMNS = ['email', 'effective_date', 'action', 'reason', 'subsidised_product_level'];
const REMOVAL_DATE_COLUMNS = ['effective_date'];

// §2.6a/EMP-04 — extra detail on a committed REMOVAL row's result line:
// when a scheduled row applies, and any pending Additions it cancelled
// instead of ending an enrolment (cancelled_pending).
function rowDetail(r) {
  const parts = [];
  if (r.status === 'scheduled' && r.effective_date) parts.push(`scheduled for ${r.effective_date}`);
  const cancelled = (r.cancelled_pending_additions || []).length;
  if (cancelled) parts.push(`cancelled ${cancelled} pending addition${cancelled === 1 ? '' : 's'}`);
  return parts.length ? ` — ${parts.join(', ')}` : '';
}

/* EMP-04 — "The system shall allow employer administrators to notify the
   ICIB admin when an employee leaves." BrandonUserStoryAPI.md's EMP-04 is
   explicit: the only endpoint is POST /companies/{company_id}/employees/
   bulk-upload with a REMOVAL row — "There is no separate single-item
   removal endpoint here — a single removal is simply a one-row file." A
   REMOVAL row identifies its target by email + subsidised_product_level
   together (ICIB-174) and ends only the matching enrolment(s), never a
   caller-picked enrolment_id directly. Same shape as ICIB-02's IntakeView
   RemovalPanel/BulkUploadPanel(REMOVAL), just employer_admin-scoped to
   their own group.

   Replaces an earlier version of this view that called the single-item
   DELETE .../enrolments/{enrolment_id} endpoint per enrolment — that
   endpoint exists and is employer_admin-accessible (README §4.4), but
   it's not what EMP-04's own story specifies. */
function LeaversView() {
  const { groupId, curGroup, groupProductsIn, bulkUploadEmployees } = useEmployer();
  // groupProducts now loads as part of EmployerContext's shared refresh()
  // batch for whichever group is selected (same strategy as ICIB admin's
  // context) — no per-view loadGroupProducts call needed here any more.
  const products = groupId ? groupProductsIn(groupId).filter((gp) => gp.is_active !== false) : [];

  return (
    <>
      <div className="eyebrow">Remove Employee</div>
      <div className="page-h"><h2>Remove Employee</h2></div>
      <p className="page-sub">Ends the selected product enrolment(s) a person holds in {curGroup()?.name || 'your employee benefits scheme'} and notifies ICIB admin.</p>

      {!groupId ? (
        <div className="card"><div className="empty"><b>No company assigned</b>Your account isn't currently assigned to a company.</div></div>
      ) : (
        <>
          <SingleRemovalPanel />
          <div className="sec-h"><h3>Or upload many at once</h3></div>
          <BulkRemovePanel curGroup={curGroup} products={products} bulkUploadEmployees={bulkUploadEmployees} />
        </>
      )}
    </>
  );
}

// Single-item Removal — same shape as ICIB-02's IntakeView RemovalPanel:
// no single-item removal-by-email endpoint exists, so this submits a
// one-row bulk-upload instead (§2.8), now naming the product(s) to end
// (ICIB-174).
function SingleRemovalPanel() {
  const { groupId, curGroup, employeesIn, employeesStatus, loadEmployees } = useEmployer();
  const { openDrawer } = useDrawer();
  useEffect(() => { loadEmployees(groupId); }, [groupId, loadEmployees]);

  // ICIB-01a's GET .../employees/summary now returns has_active_enrolment
  // per employee directly (BrandonUserStoryAPI.md ICIB-01a) — filtering on
  // it here needs nothing beyond the roster fetch above, unlike the old
  // per-employee GET .../employees/{id} drill-down this replaced.
  const rosterCount = groupId ? employeesIn(groupId).length : 0;
  const activeEmployees = groupId ? employeesIn(groupId).filter((e) => e.has_active_enrolment) : [];
  const [q, setQ] = useState('');
  // Same name/email search shape as ICIB admin's RemovalPanel, scoped to
  // this group's active-enrolment roster above.
  const employees = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return activeEmployees;
    return activeEmployees.filter((emp) => `${emp.user?.first_name || ''} ${emp.user?.last_name || ''} ${emp.user?.email || ''}`.toLowerCase().includes(needle));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, employeesIn, q]);

  // Same picker-opens-a-drawer pattern as EmployeesView's own register —
  // the drawer content (RemovalDrawer) does its own EMP-01b detail fetch
  // and owns the removal form/submit itself, same as EmployeesView's
  // EmployeeDrawer owns its read-only view of the same data.
  function open(emp) {
    openDrawer(
      `${emp.user?.first_name || ''} ${emp.user?.last_name || ''}`.trim() || emp.user?.email,
      'EMP-04 · Remove Employee',
      <RemovalDrawer gid={groupId} emp={emp} />
    );
  }

  return (
    <div className="card card-b">
      <div className="card-h" style={{ padding: 0, border: 0, marginBottom: 10 }}>
        <h4>Remove this person — {curGroup()?.name}</h4>
        <span className="spacer" />
        <span className="ep">POST /companies/{'{company_id}'}/employees/bulk-upload</span>
      </div>

      <div className="field">
        <label>Person <span className="req">*</span></label>
        <div className="filterbar" style={{ marginBottom: 0 }}>
          <input
            type="search"
            placeholder="Search name or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            disabled={employeesStatus !== 'ready'}
          />
        </div>
        {employeesStatus !== 'ready' ? (
          <div className="hint">Loading who still has an active enrolment…</div>
        ) : !activeEmployees.length ? (
          <div className="hint">{rosterCount ? 'Everyone in this company has already been removed.' : 'No one is registered in this company yet.'}</div>
        ) : (
          <div className="tbl-wrap" style={{ marginTop: 8, maxHeight: 260, overflowY: 'auto' }}>
            <table>
              <tbody>
                {employees.map((emp) => (
                  <tr key={emp.id} className="clickable" onClick={() => open(emp)}>
                    <td>
                      <span className="strong">{emp.user?.first_name} {emp.user?.last_name}</span><br />
                      <span className="mono dim">{emp.user?.email}</span>
                    </td>
                  </tr>
                ))}
                {!employees.length && (
                  <tr><td className="dim">No matches for &quot;{q}&quot;.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// Drawer content for SingleRemovalPanel — same read-only shape as
// EmployeesView's EmployeeDrawer (Contact + one card per enrolment with a
// LifeRail status badge), just with an active enrolment's card header
// carrying a checkbox instead of being purely informational, and the
// removal effective date/reason/submit form appended once at least one
// enrolment is selectable.
function RemovalDrawer({ gid, emp }) {
  const { employeeDetails, loadEmployeeDetail, groupProductsIn, bulkUploadEmployees, loadEmployees } = useEmployer();
  const { closeDrawer } = useDrawer();
  const toast = useToast();
  // loadEmployeeDetail isn't stable across renders — key off emp.id alone,
  // same pattern as EmployeesView's EmployeeDrawer.
  useEffect(() => { loadEmployeeDetail(gid, emp.id); }, [gid, emp.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const detail = employeeDetails[emp.id];
  const enrolments = detail?.enrolments || [];
  const activeEnrolments = enrolments.filter((en) => en.status !== 'removed' && en.status !== 'archived');
  const products = gid ? groupProductsIn(gid) : [];
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [effectiveDate, setEffectiveDate] = useState(today());
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  function toggleProduct(productId) {
    setSelectedProducts((cur) => (cur.includes(productId) ? cur.filter((p) => p !== productId) : [...cur, productId]));
  }

  async function submit(e) {
    e.preventDefault();
    const email = emp.user?.email;
    if (!email || !effectiveDate || !reason || !selectedProducts.length) return;
    setBusy(true);
    const res = await bulkUploadEmployees('single-removal.csv', [
      { action: 'REMOVAL', email, subsidised_product_level: selectedProducts, effective_date: effectiveDate, reason },
    ]);
    setBusy(false);
    if (res?.error) {
      toast(res.message || 'Could not remove this person.', 'bad', res.error);
      return;
    }
    const row = res?.results?.[0];
    if (!res?.committed || row?.status === 'error') {
      toast(row?.message || 'Could not remove this person.', 'bad', row?.status);
      return;
    }
    // §2.6a/EMP-04 — a product whose Addition was still waiting on
    // eligibility_days is cancelled rather than ended
    // (cancelled_pending_additions) — no enrolment existed, so no removal
    // confirmation is raised for it.
    const ended = row.enrolments?.length || 0;
    const cancelled = row.cancelled_pending_additions?.length || 0;
    const cancelledText = `${cancelled} pending addition${cancelled === 1 ? '' : 's'}`;
    if (row.status === 'scheduled') {
      toast(`Scheduled for ${row.effective_date} — will apply automatically that day.`, 'ok');
    } else if (!ended) {
      toast(`Cancelled ${cancelledText} — they left before becoming eligible, so nothing was enrolled.`, 'ok');
    } else {
      toast(`Removed — ended ${ended} enrolment${ended === 1 ? '' : 's'}${cancelled ? `, cancelled ${cancelledText}` : ''}. Awaiting confirmation.`, 'ok');
      // Refetch the roster summary so this person's has_active_enrolment
      // flips to false and they drop out of the picker behind this drawer.
      // Not needed on the other branches — nothing actually ended there.
      await loadEmployees(gid);
    }
    closeDrawer();
  }

  return (
    <>
      <div className="sec-h" style={{ marginTop: 0 }}><h3>Enrolments</h3><span className="spacer" /><span className="ep">POST .../bulk-upload</span></div>
      {!detail ? (
        <div className="empty"><b>Loading…</b></div>
      ) : enrolments.length ? (
        <form onSubmit={submit}>
          {enrolments.map((en) => {
            const isActive = en.status !== 'removed' && en.status !== 'archived';
            return (
              <div className="card card-b" key={en.id} style={{ marginBottom: 10 }}>
                <div className="card-h" style={{ padding: 0, border: 0, marginBottom: 8 }}>
                  {isActive ? (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input type="checkbox" checked={selectedProducts.includes(en.product_id)} onChange={() => toggleProduct(en.product_id)} />
                      <b>{products.find((p) => p.id === en.product_id)?.name || en.product_id}</b>
                    </label>
                  ) : (
                    <b>{products.find((p) => p.id === en.product_id)?.name || en.product_id}</b>
                  )}
                  <span className="spacer" />
                  <LifeRail status={en.status} withLabel />
                </div>
                <DefinitionList rows={[
                  ['Invited', fmtDT(en.invited_at)],
                  ['Employment effective date', fmtDate(en.effective_date)],
                  ['Reason', en.reason || '—'],
                  ['Removal reason', en.removal_reason || '—'],
                  ['Removal effective date', fmtDate(en.removal_effective_date)],
                ]} />
              </div>
            );
          })}

          {activeEnrolments.length ? (
            <>
              <div className="field">
                <label>Removal effective date <span className="req">*</span></label>
                <input type="date" required value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
                <div className="hint">Today or earlier ends the selected enrolment(s) now. A future date (e.g. their last day) schedules it instead.</div>
              </div>
              <div className="field">
                <label>Reason <span className="req">*</span></label>
                <input required value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Resigned" />
              </div>
              <div className="note warn">
                Ends only the enrolment(s) checked above — pick more than one to end several
                at once. Each one is set to <span className="ep">removed</span> and immediately flagged
                for ICIB admin's confirmation once the insurer has actioned it.
              </div>
              <div className="btn-row" style={{ marginTop: 14 }}>
                <button type="submit" className="btn danger" disabled={busy || !effectiveDate || !reason || !selectedProducts.length}>{busy ? 'Removing…' : 'Remove person'}</button>
              </div>
            </>
          ) : (
            <div className="hint">No active enrolments left to remove.</div>
          )}
        </form>
      ) : <div className="empty"><b>No enrolments</b></div>}
    </>
  );
}

// REMOVAL bulk — same underlying endpoint/contract as ICIB-02's IntakeView
// BulkUploadPanel(REMOVAL tab), just employer_admin-scoped to their own
// group. No "process one at a time" alternative, unlike EMP-03's Addition
// — there's no single-item removal-by-email endpoint to call per row.
function BulkRemovePanel({ curGroup, products, bulkUploadEmployees }) {
  const toast = useToast();
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState([]);
  // A filled-in template can run to hundreds of rows — page the preview
  // rather than rendering the whole file at once. Upload still sends
  // every row, not just the visible page.
  const rowsPage = usePagination(rows);
  const [busy, setBusy] = useState(false);
  const [buildingXlsx, setBuildingXlsx] = useState(false);
  const [result, setResult] = useState(null);
  // The per-row log is as long as the file — paged like the preview.
  const resultPage = usePagination(result?.results || []);

  const headers = rows.length ? Object.keys(rows[0]) : [];
  const mismatched = rows.filter((r) => r.action && r.action.toUpperCase() !== 'REMOVAL').length;
  // EXTR-13 — column headings follow ICIB's own sheet, same as IntakeView.
  const headings = columnHeadings([], products);

  async function onDownloadTemplate() {
    const cols = withProductColumns(REMOVAL_COLUMNS, products);
    // Product columns default to No on the starter row, so only the
    // product(s) explicitly switched to Yes get ended.
    const productNames = new Set(products.map((p) => p.name));
    const starterRow = cols.map((c) => (c === 'action' ? 'REMOVAL' : c === 'effective_date' ? today() : productNames.has(c) ? 'No' : ''));
    const base = `${curGroup()?.company_code || 'company'}_removal_template`;
    setBuildingXlsx(true);
    try {
      const { downloadXlsxTemplate } = await loadXlsxUtils();
      await downloadXlsxTemplate({
        filename: `${base}.xlsx`,
        starterRow,
        icibStyle: true,
        ...withHeadings({ headers: cols, listDropdowns: productDropdowns(products), dateColumns: REMOVAL_DATE_COLUMNS }, headings),
      });
    } finally {
      setBuildingXlsx(false);
    }
  }

  function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setResult(null);
    setFileName(file.name);
    rowsPage.setPage(1);
    if (/\.xlsx$/i.test(file.name)) {
      loadXlsxUtils().then(({ parseXlsx }) => file.arrayBuffer().then(parseXlsx)).then((parsed) => setRows(fromHeadings(parsed, headings)));
    } else {
      file.text().then((text) => setRows(fromHeadings(parseCsv(text), headings)));
    }
  }


  async function onUpload() {
    if (!rows.length) return;
    // ICIB-174 — fold each row's Yes/No product columns into the id array
    // the bulk endpoint expects; company_code isn't sent (utils/productColumns.js).
    const { rows: uploadRows, problems } = prepareUploadRows(rows, products);
    if (problems.length) { toast(problems.join(' '), 'bad'); return; }
    if (!uploadRows.length) { toast('No filled-in rows to upload.', 'bad'); return; }
    setBusy(true);
    const res = await bulkUploadEmployees(fileName, uploadRows);
    setBusy(false);
    if (res?.error) { toast(res.message || 'Could not process this file.', 'bad', res.error); return; }
    setResult({ ...res, sentRows: uploadRows });
    resultPage.setPage(1);
    if (res.committed) {
      toast(`${res.rows_processed} row(s) committed.`, 'ok');
      setFileName(''); setRows([]);
    } else {
      toast('Some rows failed — nothing was written. Fix and re-upload.', 'bad');
    }
  }

  return (
    <div className="card card-b">
      <div className="card-h" style={{ padding: 0, border: 0, marginBottom: 10 }}>
        <h4>Bulk removal</h4>
        <span className="spacer" />
        <span className="ep">POST /companies/{'{company_id}'}/employees/bulk-upload</span>
      </div>

      <div className="btn-row" style={{ marginBottom: 6 }}>
        <button type="button" className="btn sm" onClick={onDownloadTemplate} disabled={buildingXlsx}>
          {buildingXlsx ? 'Building…' : 'Download removal template (.xlsx)'}
        </button>
      </div>
      <div className="hint" style={{ marginBottom: 12 }}>
        The template has just the columns a removal row needs — action pre-filled. Each product
        has its own column with a Yes/No dropdown — mark Yes for every product whose enrolment
        should end. Effective date is formatted
        YYYY-MM-DD with a note on the header cell.
      </div>
      <div className="field">
        <label>Completed file</label>
        <input type="file" accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={onFile} />
        <div className="hint">The .xlsx template above, or a plain CSV with the same headers — either uploads the same way.</div>
      </div>

      {rows.length > 0 && (
        <>
          {mismatched > 0 && (
            <div className="note warn" style={{ marginBottom: 10 }}>
              {mismatched} row(s) have an action other than REMOVAL — that's fine, each row is
              processed by its own action column.
            </div>
          )}
          <div className="tbl-wrap" style={{ marginBottom: 10 }}>
            <table>
              <thead><tr>{headers.map((h) => <th key={h}>{h}</th>)}</tr></thead>
              <tbody>
                {rowsPage.pageItems.map((r, i) => (
                  <tr key={i}>{headers.map((h) => <td key={h} className="mono">{r[h]}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginBottom: 10 }}><Pagination {...rowsPage} /></div>
          <div className="btn-row">
            <button type="button" className="btn pri sm" disabled={busy} onClick={onUpload}>{busy ? 'Uploading…' : `Upload ${rows.length} row(s) as one batch`}</button>
          </div>
          <div className="hint" style={{ marginTop: 6 }}>
            Atomic (README §2.6) — every row must pass or nothing is written. No "process one at a
            time" option here, unlike Addition — there's no single-item removal-by-email endpoint
            to call per row (BrandonUserStoryAPI.md EMP-04).
          </div>
        </>
      )}

      {result && (
        <div className={'note' + (result.committed ? ' ok' : ' bad')} style={{ marginTop: 12 }}>
          <b>{result.committed ? 'Committed' : 'Rejected — nothing written'}</b>
          {resultPage.pageItems.map((r) => (
            <div key={r.row} className="mono" style={{ fontSize: 11.5 }}>
              row {r.row} ({r.action}){resultRowEmail(result, r) && ` ${resultRowEmail(result, r)}`}: {r.status}{r.message ? ` — ${r.message}` : ''}
              {rowDetail(r)}
            </div>
          ))}
          <Pagination {...resultPage} />
        </div>
      )}
    </div>
  );
}

export default LeaversView;
